/* eslint-disable newline-before-return */
import {
  FormField,
  FormOption,
  ParsedForm,
  RawField,
} from "@/components/FormEngine/types";

/**
 * Normalise ParsedForm en JSON consommable par l'UI.
 * `overrides` est un flatMap indexé par path.join('.') (ex: "productCreationForm.test_ifs.b")
 * et peut surcharger: hidden, writable, required, value (primitive).
 */
export const normalizeJson = (
  form: ParsedForm,
  overrides?: Record<string, Partial<RawField>>,
) => {
  const steps = Array.from(form.steps.entries()).map(([stepIndex, stepObj]) => {
    const sections = Array.from(stepObj.sections.entries()).map(
      ([sectionIndex, sectionObj]) => {
        const subSections = Array.from(sectionObj.subSections.entries()).map(
          ([subSectionIndex, subSectionObj]) => {
            const rows = subSectionObj.rows.map((row) => ({
              index: parseInt(row.id.replace("row-", ""), 10),
              fields: row.fields.map((f: FormField) => {
                const key = f.path.join(".");
                const ov = overrides?.[key];

                const hidden =
                  typeof ov?.hidden === "boolean" ? ov.hidden : f.hidden;
                const writable =
                  typeof ov?.writable === "boolean" ? ov.writable : f.writable;
                const required =
                  typeof ov?.required === "boolean" ? ov.required : f.required;

                // si override.value existe:
                // - pour select/radiobox: value.id = String(override), value.value = idem (id sémantique), text conservé par ailleurs
                // - pour les autres: value.value = primitive, id conservé
                const value =
                  ov && "value" in ov
                    ? ((): { id: string; value: any } => {
                        const ovVal: any = (ov as any).value;
                        const isPrimitive =
                          typeof ovVal === "string" ||
                          typeof ovVal === "number" ||
                          typeof ovVal === "boolean" ||
                          ovVal === null;
                        if (!isPrimitive) {
                          return f.value;
                        }
                        const isSelect =
                          typeof f.type === "string" &&
                          (f.type.toLowerCase() === "select" ||
                            f.type.toLowerCase() === "radiobox");
                        if (isSelect) {
                          return {
                            id: String(ovVal ?? ""),
                            value: String(ovVal ?? ""),
                          };
                        }
                        return {
                          id: f.value?.id ?? "",
                          value: ovVal,
                        };
                      })()
                    : f.value;

                return {
                  type: f.type,
                  name: f.name,
                  value,
                  id: f.id,
                  path: f.path,
                  required,
                  hidden,
                  initialField: f.initialField,
                  writable,
                  defaultValue: undefined,
                  options: f.options?.map((o: FormOption) => ({
                    id: o.id,
                    value: o.value,
                    impacts: o.impacts,
                  })),
                  layout: {
                    step: f.step,
                    section: f.section,
                    subSection: f.subSection,
                    row: f.row,
                    col: f.col,
                  },
                  multiple:
                    typeof f.type === "string" &&
                    f.type.toLowerCase() === "multiselect"
                      ? true
                      : undefined,
                  decorator: f.decorator,
                  validators: [],
                };
              }),
            }));

            return { index: subSectionIndex, rows };
          },
        );

        return { index: sectionIndex, subSections };
      },
    );

    return { index: stepIndex, sections };
  });

  const anomalies = form.invalidFields.map((inf) => {
    const path = inf.path?.length ? inf.path.join(".") : "<root>";
    const details = inf.errors?.length
      ? inf.errors.map((e) => `${e.key}: ${e.message}`).join(", ")
      : "unknown";
    return `Invalid field at "${path}" → ${details}`;
  });

  return { steps, anomalies };
};
